// @ts-nocheck
import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, formatDate } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";

function SimplePagination({
  page,
  setPage,
  totalPages,
  isFetching,
}: {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  isFetching: boolean;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-4 text-xs">
      <span className="text-slate-500 mr-2">
        Trang {page} / {totalPages}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page <= 1 || isFetching}
        onClick={() => setPage(page - 1)}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page >= totalPages || isFetching}
        onClick={() => setPage(page + 1)}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
      {isFetching && (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-2" />
      )}
    </div>
  );
}

// ── ORDERS TAB ──
export function CustomerOrdersTab({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer-orders-paginated", customerId, page],
    queryFn: async () =>
      trpcClient.orders.list.query({ customerId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const ordersList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !ordersList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={() =>
            navigate({
              to: "/orders/create",
              search: { customerId, customerName } as any,
            })
          }
        >
          <Plus className="h-3.5 w-3.5" /> Tạo đơn hàng
        </Button>
      </div>
      {ordersList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có đơn hàng
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mã ĐH</TableHead>
                <TableHead>Ngày đặt</TableHead>
                <TableHead>Hạn giao</TableHead>
                <TableHead className="text-right">SL</TableHead>
                <TableHead className="text-right">Tổng tiền</TableHead>
                <TableHead className="text-right">Đã giao</TableHead>
                <TableHead>NV KD</TableHead>
                <TableHead>Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordersList.map((o: any) => {
                const oCfg = getStatusConfig("OrderStatus", o.status);
                const totalQty =
                  o.orderDetails?.reduce(
                    (s: number, d: any) => s + (d.quantity || 0),
                    0,
                  ) || 0;
                const deliveredQty =
                  (o.deliveredQty ??
                    o.orderDetails?.reduce(
                      (s: number, d: any) => s + (d.qtyDelivered || 0),
                      0,
                    )) ||
                  0;
                const pct =
                  totalQty > 0
                    ? Math.round((deliveredQty / totalQty) * 100)
                    : 0;
                return (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/orders/$orderId",
                        params: { orderId: o.id },
                      })
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium text-indigo-600">
                      <EntityLink
                        entityType="order"
                        id={o.id}
                        code={o.orderId}
                        search={{ returnUrl: `/customers/${customerId}?tab=orders` }}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtD(o.orderDate)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtD(o.deliveryDeadline)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {totalQty > 0 ? totalQty.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatMoney(o.totalAmount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={`font-mono text-[11px] font-medium ${pct >= 100 ? "text-emerald-600" : pct > 0 ? "text-amber-600" : "text-slate-600"}`}
                        >
                          {deliveredQty.toLocaleString()}/
                          {totalQty.toLocaleString()}
                        </span>
                        <div className="w-10 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : pct > 0 ? "bg-amber-400" : "bg-slate-200"}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[80px] truncate">
                      {o.salesUser?.fullName || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={oCfg.color}>
                        {oCfg.label || o.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── QUOTATIONS TAB ──
export function CustomerQuotationsTab({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer-quotations-paginated", customerId, page],
    queryFn: async () =>
      trpcClient.quotations.list.query({ customerId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const quotationsList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !quotationsList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={() =>
            navigate({
              to: "/quotations/create",
              search: { customerId, customerName } as any,
            })
          }
        >
          <Plus className="h-3.5 w-3.5" /> Tạo báo giá
        </Button>
      </div>
      {quotationsList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có báo giá
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mã BG</TableHead>
                <TableHead>Ngày tạo</TableHead>
                <TableHead>Hiệu lực</TableHead>
                <TableHead className="text-right">Giá trị</TableHead>
                <TableHead>NV KD</TableHead>
                <TableHead>Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotationsList.map((qt: any) => {
                const qCfg = getStatusConfig("QuotationStatus", qt.status);
                const totalValue =
                  qt.details?.reduce(
                    (s: number, d: any) =>
                      s + Number(d.quantity || 0) * Number(d.priceVND || 0),
                    0,
                  ) ||
                  qt.totalAmount ||
                  0;
                return (
                  <TableRow
                    key={qt.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/quotations/$quotationId",
                        params: { quotationId: qt.id },
                      })
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium text-indigo-600">
                      <EntityLink
                        entityType="quotation"
                        id={qt.id}
                        code={qt.quotationId}
                        search={{ returnUrl: `/customers/${customerId}?tab=quotations` }}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtD(qt.createdDate)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtD(qt.validUntil)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatMoney(totalValue)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[80px] truncate">
                      {qt.creator?.fullName || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={qCfg.color}>
                        {qCfg.label || qt.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── PRODUCTS TAB ──
export function CustomerProductsTab({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer-products-paginated", customerId, page],
    queryFn: async () =>
      trpcClient.products.list.query({ customerId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const displayProducts = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !displayProducts.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      {displayProducts.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có sản phẩm
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mã SP</TableHead>
                <TableHead>Tên sản phẩm</TableHead>
                <TableHead>Loại</TableHead>
                <TableHead>ĐVT</TableHead>
                <TableHead className="text-right">Giá bán</TableHead>
                <TableHead>Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayProducts.map((p: any) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() =>
                    navigate({
                      to: "/products/$productId",
                      params: { productId: p.id },
                    })
                  }
                >
                  <TableCell className="font-mono text-xs font-medium text-indigo-600">
                    <EntityLink
                      entityType="product"
                      id={p.id}
                      code={p.productCode}
                      search={{ returnUrl: `/customers/${customerId}?tab=products` }}
                    />
                  </TableCell>
                  <TableCell className="text-xs truncate max-w-[200px]">
                    <EntityLink
                      entityType="product"
                      id={p.id}
                      label={p.productName}
                      search={{ returnUrl: `/customers/${customerId}?tab=products` }}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {p.productType?.group || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {p.unit || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(p.price)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={p.active ? "default" : "secondary"}
                      className="text-[10px] px-1.5 py-0"
                    >
                      {p.active ? "Đang SX" : "Ngưng"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── DELIVERIES TAB ──
export function CustomerDeliveriesTab({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer-deliveries-paginated", customerId, page],
    queryFn: async () =>
      trpcClient.deliveries.list.query({ customerId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const deliveriesList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !deliveriesList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      {deliveriesList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có phiếu giao hàng
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mã GH</TableHead>
                <TableHead>Ngày giao</TableHead>
                <TableHead className="text-right">Số lượng</TableHead>
                <TableHead className="text-right">Thành tiền</TableHead>
                <TableHead>Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveriesList.map((d: any) => {
                const dCfg = getStatusConfig("DeliveryStatus", d.status);
                return (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/delivery/$deliveryId",
                        params: { deliveryId: d.id },
                      })
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium text-indigo-600">
                      <EntityLink
                        entityType="delivery"
                        id={d.id}
                        code={d.deliveryId}
                        search={{ returnUrl: `/customers/${customerId}?tab=deliveries` }}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtD(d.deliveryDate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatNumber(
                        d.deliveryItems?.reduce(
                          (s: number, di: any) =>
                            s + (Number(di.deliveredQty) || 0),
                          0,
                        ) || 0,
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-slate-800">
                      {formatMoney(d.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={dCfg.color}>
                        {dCfg.label || d.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}
